import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import {
  Globe, Search, Filter, Plus, Edit, Trash2, Check,
  X, ChevronDown, MapPin, Languages, Clock, Settings,
  Building2, FileText, Flag, CreditCard, ArrowRight,
  ArrowUpRight, ArrowDown, DollarSign, Briefcase,
  Phone, AlertCircle, Mail, Calendar, Store, ArrowLeft
} from "lucide-react";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/Button";
import { Badge } from "../../components/ui/badge";
import AdminHeader from "../../Headers/AdminHeader";
import { useRegionSettings } from "../../modules/admin/hooks/useRegionSettings";
import { useServiceArea } from "../../modules/admin/hooks/useServiceArea";
import { getAllLanguages, getAllCurrencies } from "../../redux/Actions/AuthAction";
import { useAppDispatch, useAppSelector } from "../../redux/store";
import { createServiceArea } from "../../redux/Actions/ServiceAreaAction";
import { getAllCountries, createCountry } from "../../redux/Actions/AdminAction";
import { extractLocationFromPlace, findOrCreateCountry } from "../../lib/locationUtils";
import RegionSettingsModal from "./RegionSettingsModal";

// Google Maps API TypeScript declarations
declare global {
  interface Window {
    google: any;
  }
}

const RegionSettings = () => {
  const dispatch = useAppDispatch();
  
  // Hooks
  const {
    regionSettings,
    selectedRegionSettings,
    loading,
    error,
    totalCount,
    hasNextPage,
    currentPage,
    currentLimit,
    fetchRegionSettings,
    createNewRegionSettings,
    updateExistingRegionSettings,
    deleteExistingRegionSettings,
    fetchRegionSettingsById,
    clearSelected,
    clearErrorState,
    setPage,
    setLimit
  } = useRegionSettings();

  const { fetchServiceAreas } = useServiceArea();

  // Get languages and currencies from auth state
  const { languages, currencies } = useAppSelector((state) => state.auth);
  const { serviceAreas } = useAppSelector((state) => state.serviceArea);
  const { countries } = useAppSelector((state) => state.admin);

  // Google Maps autocomplete states
  const [isGoogleMapsAvailable, setIsGoogleMapsAvailable] = useState(false);
  const [isCreatingServiceArea, setIsCreatingServiceArea] = useState(false);

  // Local state
  const [searchQuery, setSearchQuery] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(null);
  const [sortBy, setSortBy] = useState("name");
  const [sortOrder, setSortOrder] = useState("asc");
  const [statusFilter, setStatusFilter] = useState("all");
  const [currencyFilter, setCurrencyFilter] = useState("all");
  const [languageFilter, setLanguageFilter] = useState("all");
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    code: "",
    supportedLanguages: [],
    serviceAreas: [],
    quoteCurrency: { id: "" },
    defaultCurrency: { id: "" },
    defaultLanguage: { id: "" },
    supportPhone: "",
    supportEmail: "",
    businessWeekendEndHour: "",
    businessWeekendStartHour: "",
    businessWeekdayEndHour: "",
    businessWeekdayStartHour: "",
    commissionEmergencyServiceHoliday: 0,
    commissionEmergencyServiceWeekend: 0,
    commissionEmergencyServiceNight: 0,
    commissionEmergencyServiceDay: 0,
    commissionRegularService: 0,
    quoteAmount: 0
  });

  // Fetch data on component mount
  useEffect(() => {
    fetchInitialData();
  }, []);

  // Initialize Google Maps when component mounts
  useEffect(() => {
    initializeGoogleMaps();
  }, []);

  // Fetch region settings when filters change
  useEffect(() => {
    fetchRegionSettingsData();
  }, [currentPage, currentLimit, searchQuery, sortBy, sortOrder, statusFilter, currencyFilter, languageFilter]);

  const fetchInitialData = async () => {
    // Fetch languages, currencies, and service areas
    await dispatch(getAllLanguages({ isShowLoader: false }));
    await dispatch(getAllCurrencies({ isShowLoader: false }));
    await fetchServiceAreas({ page: 1, limit: 1000, isShowLoader: false });
    await dispatch(getAllCountries({ isShowLoader: false }));
  };

  const fetchRegionSettingsData = async () => {
    await fetchRegionSettings({
      page: currentPage,
      limit: currentLimit,
      search: searchQuery,
      sortBy,
      sortOrder,
      status: statusFilter,
      currency: currencyFilter,
      language: languageFilter,
      isShowLoader: true
    });
  };

  // Initialize Google Maps
  const initializeGoogleMaps = () => {
    if (window.google && window.google.maps) {
      setIsGoogleMapsAvailable(true);
    } else {
      // Check if Google Maps script is loaded
      const script = document.querySelector("script[src*=\"maps.googleapis.com\"]");
      if (script) {
        script.addEventListener("load", () => {
          setIsGoogleMapsAvailable(true);
        });
      }
    }
  };

  // Create service area from place function
  const createServiceAreaFromPlace = useCallback(async (place: any) => {
    if (!place || !place.geometry) {
      
      return;
    }

    setIsCreatingServiceArea(true);
    try {
      // Extract location data from place
      const locationData = extractLocationFromPlace(place);
      
      // Find or create country
      const countryId = await findOrCreateCountry(locationData.country, countries, createCountry, dispatch);
      
      // Create service area data
      const serviceAreaData = {
        name: locationData.city || locationData.province || "New Service Area",
        type: { id: 2 }, // City type
        radius: 50, // Default radius
        restrictions: [],
        cities: locationData.city ? [locationData.city] : [],
        postalCodes: locationData.postalCode ? [locationData.postalCode] : [],
        province: locationData.province || "",
        country: { id: countryId },
        serviceTypes: ["general"],
        emergencyServices: false
      };

      // Create the service area
      const result = await dispatch(createServiceArea({ data: serviceAreaData }));
      
      if (result.payload) {
        // Add the new service area to the form data
        setFormData(prev => ({
          ...prev,
          serviceAreas: [...prev.serviceAreas, { id: result.payload.id }]
        }));
        
        // Refresh service areas list
        await fetchServiceAreas({ page: 1, limit: 1000, isShowLoader: false });
      }
            } catch (error) {
        } finally {
      setIsCreatingServiceArea(false);
    }
  }, [countries, dispatch, fetchServiceAreas]);

  const handleSearch = (value: string) => {
    setSearchQuery(value);
    setPage(1); // Reset to first page when searching
  };

  const handleSortChange = (value: string) => {
    setSortBy(value);
    setPage(1);
  };

  const handleFilterChange = (filterType: string, value: string) => {
    switch (filterType) {
      case "status":
        setStatusFilter(value);
        break;
      case "currency":
        setCurrencyFilter(value);
        break;
      case "language":
        setLanguageFilter(value);
        break;
    }
    setPage(1);
  };

  const handlePageChange = (newPage: number) => {
    // Validate page number
    if (newPage < 1) return;
    if (!hasNextPage && newPage > currentPage) return;
    setPage(newPage);
  };

  const handleLimitChange = (newLimit: number) => {
    setLimit(newLimit);
    setPage(1); // Reset to first page when changing limit
  };

  const resetFormData = useCallback(() => {
    setFormData({
      name: "",
      code: "",
      supportedLanguages: [],
      serviceAreas: [],
      quoteCurrency: { id: "" },
      defaultCurrency: { id: "" },
      defaultLanguage: { id: "" },
      supportPhone: "",
      supportEmail: "",
      businessWeekendEndHour: "",
      businessWeekendStartHour: "",
      businessWeekdayEndHour: "",
      businessWeekdayStartHour: "",
      commissionEmergencyServiceHoliday: 0,
      commissionEmergencyServiceWeekend: 0,
      commissionEmergencyServiceNight: 0,
      commissionEmergencyServiceDay: 0,
      commissionRegularService: 0,
      quoteAmount: 0
    });
  }, []);

  const handleAddNew = useCallback(() => {
    resetFormData();
    setShowAddModal(true);
  }, [resetFormData]);

  const handleEdit = useCallback((region: any) => {
    // Map the region data to form data
    const mappedData = {
      name: region.name || "",
      code: region.code || "",
      supportedLanguages: region.supportedLanguages || [],
      serviceAreas: region.serviceAreas || [],
      quoteCurrency: region.quoteCurrency || { id: "" },
      defaultCurrency: region.defaultCurrency || { id: "" },
      defaultLanguage: region.defaultLanguage || { id: "" },
      supportPhone: region.supportPhone || "",
      supportEmail: region.supportEmail || "",
      businessWeekendEndHour: region.businessWeekendEndHour || "",
      businessWeekendStartHour: region.businessWeekendStartHour || "",
      businessWeekdayEndHour: region.businessWeekdayEndHour || "",
      businessWeekdayStartHour: region.businessWeekdayStartHour || "",
      commissionEmergencyServiceHoliday: region.commissionEmergencyServiceHoliday || 0,
      commissionEmergencyServiceWeekend: region.commissionEmergencyServiceWeekend || 0,
      commissionEmergencyServiceNight: region.commissionEmergencyServiceNight || 0,
      commissionEmergencyServiceDay: region.commissionEmergencyServiceDay || 0,
      commissionRegularService: region.commissionRegularService || 0,
      quoteAmount: region.quoteAmount || 0
    };
    setFormData(mappedData);
    setShowEditModal(region);
  }, []);

  const handleDelete = async (id: string) => {
    if (window.confirm("Are you sure you want to delete this region setting?")) {
      await deleteExistingRegionSettings(id);
      fetchRegionSettingsData();
    }
  };

  const handleSubmit = useCallback(async (data: any, isEdit: boolean = false) => {
    try {
      if (isEdit && showEditModal) {
        await updateExistingRegionSettings(showEditModal.id, data);
      } else {
        await createNewRegionSettings(data);
      }
      
      // Close modal and refresh data
      setShowAddModal(false);
      setShowEditModal(null);
      resetFormData();
      fetchRegionSettingsData();
            } catch (error) {
        }
  }, [showEditModal, updateExistingRegionSettings, createNewRegionSettings, resetFormData, fetchRegionSettingsData]);

  const handleCloseModal = useCallback(() => {
    setShowAddModal(false);
    setShowEditModal(null);
    resetFormData();
  }, [resetFormData]);

  // Helper functions to get display names
  const getLanguageName = (id: string) => {
    const language = languages?.find((lang: any) => lang.id === id);
    return language?.name || id;
  };

  const getCurrencyName = (id: string) => {
    const currency = (currencies as any[])?.find((curr: any) => curr.value === id || curr.id === id);
    return currency?.label || currency?.name || id;
  };

  const getServiceAreaName = (id: string) => {
    const serviceArea = serviceAreas?.data?.find((area: any) => area.id === id);
    return serviceArea?.name || id;
  };

  const formatTime = (timeString: string) => {
    if (!timeString) return "";
    try {
      const date = new Date(timeString);
      return date.toLocaleTimeString("en-US", { 
        hour: "2-digit", 
        minute: "2-digit",
        hour12: false 
      });
    } catch {
      return timeString;
    }
  };

  const RegionCard = ({ region }) => (
    <Card className="p-4 md:p-6" style={{ textAlign: "left" }}>
      <div className="flex flex-col md:flex-row justify-between items-start gap-4">
        <div className="flex flex-col md:flex-row items-start md:items-center gap-3 w-full md:w-auto">
          <div className="p-2 rounded-lg bg-blue-100">
            <Globe className="h-6 w-6 text-blue-600" />
          </div>
          <div className="flex-1">
            <div className="flex flex-col md:flex-row items-start md:items-center gap-2">
              <h3 className="font-semibold text-lg">{region.name}</h3>
              <Badge variant={undefined} className={
                region.status === "active" ? "bg-green-100 text-green-700" :
                "bg-yellow-100 text-yellow-700"
              }>
                {region.status?.toUpperCase() || "ACTIVE"}
              </Badge>
            </div>
            <div className="flex flex-wrap items-center gap-2 mt-1 text-sm text-gray-600">
              <div className="flex items-center gap-1">
                <Flag className="h-4 w-4" />
                {region.code}
              </div>
              <span className="hidden md:inline text-gray-400">|</span>
              <div className="flex items-center gap-1">
                <Languages className="h-4 w-4" />
                {getLanguageName(region.defaultLanguage?.id)}
              </div>
              <span className="hidden md:inline text-gray-400">|</span>
              <div className="flex items-center gap-1">
                <CreditCard className="h-4 w-4" />
                {getCurrencyName(region.defaultCurrency?.id)}
              </div>
            </div>
          </div>
        </div>
        <div className="flex gap-2 self-end md:self-start">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleEdit(region)}
          >
            <Edit className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="text-red-600"
            onClick={() => handleDelete(region.id)}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Service Areas & Languages */}
      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <h4 className="font-medium mb-3">Provinces & Areas</h4>
          <div className="space-y-3">
            {region.serviceAreas && region.serviceAreas.length > 0 ? (
              <div className="p-3 bg-gray-50 rounded-lg">
                <div className="flex flex-wrap gap-2">
                  {region.serviceAreas.map((area, index) => (
                    <Badge variant={undefined} key={index} className="bg-blue-50">
                      {getServiceAreaName(area.id)}
                    </Badge>
                  ))}
                </div>
              </div>
            ) : (
              <div className="p-3 bg-gray-50 rounded-lg text-gray-500">
                No service areas configured
              </div>
            )}
          </div>
        </div>

        <div>
          <h4 className="font-medium mb-3">Languages & Currencies</h4>
          <div className="space-y-3">
            <div className="p-3 bg-gray-50 rounded-lg">
              <div className="font-medium mb-2">Supported Languages</div>
              <div className="flex flex-wrap gap-2">
                {region.supportedLanguages && region.supportedLanguages.length > 0 ? (
                  region.supportedLanguages.map((lang, index) => (
                  <Badge key={index} variant="outline">
                      {getLanguageName(lang.id)}
                  </Badge>
                  ))
                ) : (
                  <span className="text-gray-500">No languages configured</span>
                )}
              </div>
            </div>
            <div className="p-3 bg-gray-50 rounded-lg">
              <div className="font-medium mb-2">Quote Currency</div>
              <div className="flex flex-wrap gap-2">
                <Badge variant="outline">
                  {getCurrencyName(region.quoteCurrency?.id)}
                  </Badge>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Pricing & Commission */}
      <div className="mt-6">
        <h4 className="font-medium mb-3">Pricing & Commission</h4>
        <div className="p-4 bg-gray-50 rounded-lg">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 mb-3">
                <span className="font-medium">Quote Amount</span>
                <Badge variant={undefined} className="bg-purple-100 text-purple-700">
                  {region.quoteAmount} {getCurrencyName(region.quoteCurrency?.id)}
                </Badge>
                  </div>
                </div>
                <div>
                  <div className="text-sm text-gray-600 mb-2">Commission Rates</div>
                  <div className="space-y-1">
                    <div className="flex justify-between text-sm">
                  <span>Regular Service</span>
                  <span>{region.commissionRegularService}%</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>Emergency (Day)</span>
                  <span>{region.commissionEmergencyServiceDay}%</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>Emergency (Night)</span>
                  <span>{region.commissionEmergencyServiceNight}%</span>
                    </div>
                <div className="flex justify-between text-sm">
                  <span>Emergency (Weekend)</span>
                  <span>{region.commissionEmergencyServiceWeekend}%</span>
                  </div>
                <div className="flex justify-between text-sm">
                  <span>Emergency (Holiday)</span>
                  <span>{region.commissionEmergencyServiceHoliday}%</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Business Hours & Support */}
      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <h4 className="font-medium mb-3">Operating Hours</h4>
          <div className="space-y-3">
            <div className="p-3 bg-gray-50 rounded-lg">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 mb-2">
                <span className="text-sm text-gray-600">Weekdays</span>
                <Badge variant="outline">
                  {formatTime(region.businessWeekdayStartHour)} - {formatTime(region.businessWeekdayEndHour)}
                </Badge>
              </div>
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
                <span className="text-sm text-gray-600">Weekends</span>
                <Badge variant="outline">
                  {formatTime(region.businessWeekendStartHour)} - {formatTime(region.businessWeekendEndHour)}
                </Badge>
              </div>
            </div>
          </div>
        </div>

        <div>
          <h4 className="font-medium mb-3">Support Contact</h4>
          <div className="space-y-3">
            <div className="p-3 bg-gray-50 rounded-lg">
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm">
                  <Phone className="h-4 w-4 text-gray-400" />
                  {region.supportPhone || "Not configured"}
                </div>
                <div className="flex items-center gap-2 text-sm break-all">
                  <Mail className="h-4 w-4 text-gray-400" />
                  {region.supportEmail || "Not configured"}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Created/Updated Info */}
      <div className="mt-6 pt-6 border-t">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <span className="text-sm text-gray-500">Created</span>
            <p className="font-medium mt-1">
              {region.createdAt ? new Date(region.createdAt).toLocaleDateString() : "N/A"}
            </p>
          </div>
          <div>
            <span className="text-sm text-gray-500">Last Updated</span>
            <p className="font-medium mt-1">
              {region.updatedAt ? new Date(region.updatedAt).toLocaleDateString() : "N/A"}
            </p>
          </div>
        </div>
      </div>
    </Card>
  );

  return (
    <div className="min-h-screen bg-gray-50" style={{ textAlign: "left" }}>
      <AdminHeader />
      
      <main className="lg:pl-64 pt-16">
        <div className="p-4 md:p-6">
          <div className="max-w-7xl mx-auto space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Region Settings</h1>
                <p className="text-gray-600 mt-1">Manage region-specific settings and configurations</p>
              </div>
              <Button
                onClick={handleAddNew}
                className="flex items-center gap-2 w-full sm:w-auto"
              >
                <Plus className="h-4 w-4" />
                Add Region
              </Button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <Card className="p-4 md:p-6">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-sm text-gray-500">Total Regions</p>
                    <p className="text-2xl font-semibold mt-1">{totalCount}</p>
                  </div>
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Globe className="h-5 w-5 text-blue-600" />
                  </div>
                </div>
              </Card>

              <Card className="p-4 md:p-6">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-sm text-gray-500">Active Languages</p>
                    <p className="text-2xl font-semibold mt-1">{languages?.length || 0}</p>
                  </div>
                  <div className="p-2 bg-green-100 rounded-lg">
                    <Languages className="h-5 w-5 text-green-600" />
                  </div>
                </div>
              </Card>

              <Card className="p-4 md:p-6">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-sm text-gray-500">Service Areas</p>
                    <p className="text-2xl font-semibold mt-1">{serviceAreas?.totalCount || 0}</p>
                  </div>
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <Building2 className="h-5 w-5 text-purple-600" />
                  </div>
                </div>
              </Card>

              <Card className="p-4 md:p-6">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-sm text-gray-500">Active Currencies</p>
                    <p className="text-2xl font-semibold mt-1">{currencies?.length || 0}</p>
                  </div>
                  <div className="p-2 bg-yellow-100 rounded-lg">
                    <CreditCard className="h-5 w-5 text-yellow-600" />
                  </div>
                </div>
              </Card>
            </div>

            {/* Filters */}
            <Card className="p-4">
              <div className="flex flex-col sm:flex-row justify-between gap-4">
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 flex-1">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search regions..."
                      value={searchQuery}
                      onChange={(e) => handleSearch(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border rounded-lg"
                    />
                  </div>
                  <Button
                    variant="outline"
                    className="flex items-center justify-center gap-2"
                    onClick={() => setShowFilters(!showFilters)}
                  >
                    <Filter className="h-5 w-5" />
                    Filters
                  </Button>
                </div>
                <select
                  value={sortBy}
                  onChange={(e) => handleSortChange(e.target.value)}
                  className="px-4 py-2 border rounded-lg w-full sm:w-auto"
                >
                  <option value="name">Sort by Name</option>
                  <option value="code">Sort by Code</option>
                  <option value="createdAt">Sort by Created Date</option>
                  <option value="updatedAt">Sort by Updated Date</option>
                </select>
              </div>
              
              {showFilters && (
                <div className="mt-4 pt-4 border-t grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                    <select 
                      value={statusFilter}
                      onChange={(e) => handleFilterChange("status", e.target.value)}
                      className="w-full px-3 py-2 border rounded-lg"
                    >
                      <option value="all">All Statuses</option>
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Currency</label>
                    <select 
                      value={currencyFilter}
                      onChange={(e) => handleFilterChange("currency", e.target.value)}
                      className="w-full px-3 py-2 border rounded-lg"
                    >
                      <option value="all">All Currencies</option>
                      {(currencies as any[])?.map((currency: any) => (
                        <option key={currency.value} value={currency.value}>
                          {currency.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Language</label>
                    <select 
                      value={languageFilter}
                      onChange={(e) => handleFilterChange("language", e.target.value)}
                      className="w-full px-3 py-2 border rounded-lg"
                    >
                      <option value="all">All Languages</option>
                      {languages?.map((language: any) => (
                        <option key={language.id} value={language.id}>
                          {language.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              )}
            </Card>

            {/* Regions List */}
            <div className="grid grid-cols-1 gap-6">
              {loading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                  <p className="mt-2 text-gray-600">Loading region settings...</p>
                </div>
              ) : regionSettings && regionSettings.length > 0 ? (
                regionSettings.map(region => (
                <RegionCard key={region.id} region={region} />
                ))
              ) : (
                <div className="text-center py-8">
                  <Globe className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No region settings found</h3>
                  <p className="text-gray-600 mb-4">Get started by creating your first region setting.</p>
                  <Button onClick={handleAddNew}>
                    <Plus className="h-4 w-4 mr-2" />
                    Add Region Setting
                  </Button>
                </div>
              )}
            </div>

            {/* Pagination */}
            {regionSettings && regionSettings.length > 0 && totalCount > 0 && (
              <Card className="p-4 mt-6">
                <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-700">Show</span>
                    <select
                      value={currentLimit}
                      onChange={(e) => handleLimitChange(Number(e.target.value))}
                      className="px-2 py-1 border rounded text-sm bg-white"
                    >
                      <option value={5}>5</option>
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                    </select>
                    <span className="text-sm text-gray-700">
                      {((currentPage - 1) * currentLimit) + 1} - {Math.min(currentPage * currentLimit, totalCount)} of {totalCount} results
                    </span>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage <= 1}
                      className="flex items-center gap-1 disabled:opacity-50"
                    >
                      <ArrowLeft className="h-4 w-4" />
                      Previous
                    </Button>
                    
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-700">
                        Page {currentPage}
                      </span>
                      {totalCount > 0 && (
                        <span className="text-sm text-gray-500">
                          of {Math.ceil(totalCount / currentLimit)}
                        </span>
                      )}
                    </div>
                    
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={!hasNextPage}
                      className="flex items-center gap-1 disabled:opacity-50"
                    >
                      Next
                      <ArrowRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </Card>
            )}

            {/* Modal */}
            {(showAddModal || showEditModal) && (
              <RegionSettingsModal
                region={showEditModal}
                isOpen={true}
                onClose={handleCloseModal}
                onSubmit={handleSubmit}
                formData={formData}
                setFormData={setFormData}
                languages={languages || []}
                currencies={currencies || []}
                serviceAreas={serviceAreas}
                createServiceAreaFromPlace={createServiceAreaFromPlace}
                isCreatingServiceArea={isCreatingServiceArea}
                isGoogleMapsAvailable={isGoogleMapsAvailable}
              />
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default RegionSettings;
